import * as THREE from 'three';
import type { WorldPos } from './measureUtils';

export interface ActiveMeasurement {
  points: Array<{ x: number; y: number; z: number }>;
  mode: string;
  length: number | null;
  area: number | null;
}

export interface ActiveContextSelection {
  mode: 'click' | 'box' | 'lasso';
  worldCenter: { x: number; y: number; z: number };
  worldBounds: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
  orientedBounds?: {
    center: { x: number; y: number; z: number };
    size: { x: number; y: number; z: number };
    quaternion: [number, number, number, number];
  };
  samplePoints: Array<{ x: number; y: number; z: number }>;
  hitCount: number;
  attemptedSamples: number;
  confidence: 'high' | 'medium' | 'low';
  screenShape?: {
    kind: 'click' | 'box' | 'lasso';
    points: Array<{ x: number; y: number }>; // normalized canvas coords 0–1
  };
}

export interface ViewerSpatialContext {
  cameraWorld?: WorldPos;
  lookAtWorld?: WorldPos;
  lookAtSource?: 'raycast' | 'camera-forward';
  activeMeasurement?: ActiveMeasurement;
  activeContextSelection?: ActiveContextSelection;
  lastPlacedModelWorldPos?: [number, number, number];
  /** Index of the currently-visible stage in a multi-scene (construction timelapse) viewer. */
  currentStageIndex?: number;
  /** Human-readable label of the currently-visible stage (e.g. "March 2025"). */
  currentStageLabel?: string;
}

export interface ViewerPickingLike {
  camera?: THREE.Camera & { position: THREE.Vector3 };
  raycaster?: {
    setFromCameraAndScreenPosition: (
      camera: THREE.Camera,
      screenPosition: THREE.Vector2,
      screenDimensions: THREE.Vector2
    ) => void;
    intersectSplatMesh: (splatMesh: unknown, outHits: unknown[]) => unknown;
  };
  splatMesh?: unknown;
  getRenderDimensions?: (outDimensions: THREE.Vector2) => void;
  renderer?: {
    domElement?: HTMLCanvasElement;
  };
}

function hitToWorldPos(hit: unknown): WorldPos | null {
  const origin = (hit as { origin?: { x?: unknown; y?: unknown; z?: unknown } } | null)?.origin;
  const x = Number(origin?.x);
  const y = Number(origin?.y);
  const z = Number(origin?.z);

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  return { x, y, z };
}

function intersectSplatAtScreenPosition(
  viewer: ViewerPickingLike | null,
  screenPosition: THREE.Vector2,
  renderDimensions: THREE.Vector2
): WorldPos | null {
  const camera = viewer?.camera;
  const raycaster = viewer?.raycaster;
  const splatMesh = viewer?.splatMesh;

  if (!camera || !raycaster || !splatMesh) {
    return null;
  }

  camera.updateMatrixWorld?.();
  raycaster.setFromCameraAndScreenPosition(camera, screenPosition, renderDimensions);

  const outHits: unknown[] = [];
  const returnValue = raycaster.intersectSplatMesh(splatMesh, outHits);
  const hits = Array.isArray(returnValue) ? returnValue : outHits;

  return hits.length > 0 ? hitToWorldPos(hits[0]) : null;
}

export function pickWorldPointFromPointer(
  viewer: ViewerPickingLike | null,
  clientX: number,
  clientY: number
): WorldPos | null {
  const canvas = viewer?.renderer?.domElement;
  const getRenderDimensions = viewer?.getRenderDimensions;

  if (!canvas || !getRenderDimensions) {
    return null;
  }

  const rect = canvas.getBoundingClientRect();
  const renderDimensions = new THREE.Vector2();
  getRenderDimensions.call(viewer, renderDimensions);

  return intersectSplatAtScreenPosition(
    viewer,
    new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * renderDimensions.x,
      ((clientY - rect.top) / rect.height) * renderDimensions.y
    ),
    renderDimensions
  );
}

export function pickWorldPointFromViewportCenter(viewer: ViewerPickingLike | null): WorldPos | null {
  const getRenderDimensions = viewer?.getRenderDimensions;

  if (!getRenderDimensions) {
    return null;
  }

  const renderDimensions = new THREE.Vector2();
  getRenderDimensions.call(viewer, renderDimensions);

  return intersectSplatAtScreenPosition(
    viewer,
    new THREE.Vector2(renderDimensions.x / 2, renderDimensions.y / 2),
    renderDimensions
  );
}

export function getCameraForwardPoint(viewer: ViewerPickingLike | null, distance = 8): WorldPos | null {
  const camera = viewer?.camera;
  if (!camera) return null;

  const direction = new THREE.Vector3();
  camera.getWorldDirection(direction);

  return {
    x: camera.position.x + direction.x * distance,
    y: camera.position.y + direction.y * distance,
    z: camera.position.z + direction.z * distance,
  };
}

export function getViewerSpatialContext(viewer: ViewerPickingLike | null): ViewerSpatialContext | null {
  const camera = viewer?.camera;
  if (!camera) return null;

  const raycastLookAt = pickWorldPointFromViewportCenter(viewer);
  const fallbackLookAt = raycastLookAt ? null : getCameraForwardPoint(viewer);

  return {
    cameraWorld: {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
    },
    lookAtWorld: raycastLookAt ?? fallbackLookAt ?? undefined,
    lookAtSource: raycastLookAt ? 'raycast' : fallbackLookAt ? 'camera-forward' : undefined,
  };
}
